import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { medicPortalAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { trackRecentView } from '../../hooks/useRecentlyViewed';
import { fmt } from '../../lib/currency';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  CalendarDays, 
  Clock,
  MapPin,
  PoundSterling,
  ChevronRight,
  History,
  CalendarCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const STATUS_STYLES = {
  pending: 'bg-[#FFC04D]/15 text-[#FFC04D] border-[#FFC04D]/20',
  confirmed: 'bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20',
  standby: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20',
  completed: 'bg-gray-500/15 text-gray-400 border-gray-500/20',
  cancelled: 'bg-red-500/15 text-red-400 border-red-500/20',
};

const TYPE_STYLES = {
  regular: 'bg-[#1ABC9C]/15 text-[#1ABC9C]',
  night: 'bg-indigo-500/15 text-indigo-400',
  standby: 'bg-[#FFC04D]/15 text-[#FFC04D]',
  emergency: 'bg-red-500/15 text-red-400',
};

export default function MedicShifts() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const orgCurrency = user?.organisation_currency || 'GBP';
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [pastShifts, setPastShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [upcomingRes, pastRes] = await Promise.all([
        medicPortalAPI.getUpcomingShifts(),
        medicPortalAPI.getPastShifts(),
      ]);
      
      setUpcomingShifts(upcomingRes.data);
      setPastShifts(pastRes.data);
    } catch (error) {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };

  // Calculate total earnings — iter64. ``rate_payable`` is per-hour,
  // so multiply by the shift's scheduled hours. (start_time is
  // HH:MM, end_time HH:MM; same-day shifts; overnight shifts cross
  // midnight — mirror backend's _shift_hours.)
  const shiftHours = (shift) => {
    try {
      const [sh, sm] = (shift.start_time || '0:0').split(':').map(Number);
      const [eh, em] = (shift.end_time || '0:0').split(':').map(Number);
      let start = sh * 60 + sm;
      let end = eh * 60 + em;
      if (end <= start) end += 24 * 60;
      return (end - start) / 60;
    } catch { return 0; }
  };
  const calculateTotalEarnings = (shifts) => {
    return shifts.reduce((sum, s) => sum + (Number(s.rate_payable) || 0) * shiftHours(s), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-[#1ABC9C]/20 border-t-[#1ABC9C] rounded-full animate-spin" />
      </div>
    );
  }

  const isCheckinEligible = (shift) => {
    if (!shift) return false;
    if (shift.checked_in_at || shift.checked_out_at) return false;
    if (['cancelled', 'completed'].includes(shift.status)) return false;
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      return (shift.date || '').substring(0, 10) === today;
    } catch { return false; }
  };

  const ShiftCard = ({ shift }) => (
    <Card 
      className="bg-[#2B2B2B] border-white/5 hover:border-[#1ABC9C]/20 transition-colors cursor-pointer group"
      onClick={() => {
        // iter72.6 — medic portal also feeds the Recently viewed list
        // so returning to a shift being checked in on is one tap.
        trackRecentView(user?.id, {
          type: 'shift',
          id: shift.id,
          label: `${shift.location || 'Shift'} · ${shift.date}`,
          subtitle: `${shift.start_time}–${shift.end_time}`,
          href: `/portal/shifts/${shift.id}`,
        });
        navigate(`/portal/shifts/${shift.id}`);
      }}
      data-testid={`shift-card-${shift.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-3 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={STATUS_STYLES[shift.status]}>
                {shift.status}
              </Badge>
              <Badge className={`${TYPE_STYLES[shift.shift_type]} border-0`}>
                {shift.shift_type}
              </Badge>
              {shift.checked_in_at && !shift.checked_out_at && (
                <Badge className="bg-[#1ABC9C]/15 text-[#1ABC9C] border-[#1ABC9C]/20">
                  In progress
                </Badge>
              )}
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-[#F2F2F2]">
                <CalendarDays className="w-4 h-4 text-[#1ABC9C]" />
                <span className="font-medium">
                  {format(new Date(shift.date), 'EEEE, MMMM dd, yyyy')}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[#A1A1AA]">
                <Clock className="w-4 h-4" />
                <span className="font-['JetBrains_Mono']">
                  {shift.start_time} - {shift.end_time}
                </span>
              </div>
              <div className="flex items-center gap-2 text-[#A1A1AA]">
                <MapPin className="w-4 h-4" />
                <span>{shift.location}</span>
              </div>
            </div>

            {isCheckinEligible(shift) && (
              <Button
                size="sm"
                className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90 text-white"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/portal/shifts/${shift.id}#checkin`);
                }}
                data-testid={`shift-checkin-btn-${shift.id}`}
              >
                <MapPin className="w-4 h-4 mr-2" />
                Check In
              </Button>
            )}
          </div>
          
          <div className="flex flex-col items-end gap-2">
            <div className="text-right">
              <p className="text-lg font-bold text-[#1ABC9C] font-['JetBrains_Mono']">
                {fmt(shift.rate_payable, shift.currency)}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-[#A1A1AA] group-hover:text-[#1ABC9C] transition-colors" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="medic-shifts-container">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <CalendarCheck className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Upcoming</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {upcomingShifts.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#A1A1AA]/15">
                <History className="w-5 h-5 text-[#A1A1AA]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Completed</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {pastShifts.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#FFC04D]/15">
                <PoundSterling className="w-5 h-5 text-[#FFC04D]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Upcoming Earnings</p>
                <p className="text-xl font-bold text-[#F2F2F2] font-['JetBrains_Mono']">
                  {fmt(calculateTotalEarnings(upcomingShifts), orgCurrency)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#2B2B2B] border-white/5">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#1ABC9C]/15">
                <PoundSterling className="w-5 h-5 text-[#1ABC9C]" />
              </div>
              <div>
                <p className="text-sm text-[#A1A1AA]">Past Earnings</p>
                <p className="text-xl font-bold text-[#1ABC9C] font-['JetBrains_Mono']">
                  {fmt(calculateTotalEarnings(pastShifts), orgCurrency)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Shifts Tabs */}
      <Tabs defaultValue="upcoming" className="w-full">
        <TabsList className="bg-[#2B2B2B] border-white/5">
          <TabsTrigger 
            value="upcoming" 
            className="data-[state=active]:bg-[#1ABC9C]/20 data-[state=active]:text-[#1ABC9C]"
            data-testid="upcoming-shifts-tab"
          >
            Upcoming ({upcomingShifts.length})
          </TabsTrigger>
          <TabsTrigger 
            value="past" 
            className="data-[state=active]:bg-[#1ABC9C]/20 data-[state=active]:text-[#1ABC9C]"
            data-testid="past-shifts-tab"
          >
            Past ({pastShifts.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="mt-6">
          {upcomingShifts.length === 0 ? (
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardContent className="p-8 text-center">
                <CalendarDays className="w-12 h-12 mx-auto mb-3 text-[#A1A1AA] opacity-50" />
                <p className="text-[#A1A1AA]">No upcoming shifts assigned</p>
                <Button 
                  variant="outline" 
                  className="mt-4 border-[#1ABC9C]/30 text-[#1ABC9C] hover:bg-[#1ABC9C]/10"
                  onClick={() => navigate('/portal/open-shifts')}
                >
                  Browse Open Shifts
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {upcomingShifts.map(shift => (
                <ShiftCard key={shift.id} shift={shift} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="past" className="mt-6">
          {pastShifts.length === 0 ? (
            <Card className="bg-[#2B2B2B] border-white/5">
              <CardContent className="p-8 text-center">
                <History className="w-12 h-12 mx-auto mb-3 text-[#A1A1AA] opacity-50" />
                <p className="text-[#A1A1AA]">No past shifts</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {pastShifts.map(shift => (
                <ShiftCard key={shift.id} shift={shift} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
